import { observer } from "mobx-react-lite";
import { useState } from "react";

import {
  DefaultButton,
  IButtonProps,
  SplitButton,
  Stack,
  Text
} from "@bps/fluent-ui";
import { notificationMessages } from "@libs/constants/notification-messages.constants.ts";
import { PaymentMethod } from "@libs/gateways/billing/BillingGateway.dtos.ts";
import { currencyFormat } from "@libs/utils/currency.utils.ts";
import { closeInvoiceOrPaymentPage } from "@modules/billing/screens/invoice/components/utils.ts";
import { CreditNote } from "@stores/billing/models/CreditNote.ts";
import { useStores } from "@stores/hooks/useStores.ts";

import { CancelTransactionDialog } from "../../shared-components/CancelTransactionDialog.tsx";
import { RefundConfirmationDialog } from "../../shared-components/RefundConfirmationDialog.tsx";

export interface CreditNoteViewButtonsProps {
  creditNote: CreditNote;
}

const CANCEL_TEXT = "Cancel credit";

export const CreditNoteViewButtons: React.FC<CreditNoteViewButtonsProps> =
  observer(({ creditNote }) => {
    const { routing, billing, notification } = useStores();

    const [refundDialogVisible, setRefundDialogVisible] = useState(false);
    const [showCancelCreditDialog, setShowCancelCreditDialog] = useState(false);

    const handleCancel = async (reason: string) => {
      await billing.cancelCreditNote(creditNote.id, reason);
      close();
      notification.success(
        notificationMessages.creditNoteCancelled(creditNote.number)
      );
    };

    const onClear = () => {
      setRefundDialogVisible(false);
    };

    const onSucceeded = async (refundPaymentMethod: PaymentMethod) => {
      await billing.refundUnallocatedCredit(creditNote, refundPaymentMethod);
      closeInvoiceOrPaymentPage(routing, "replace");
      onClear();
    };

    const close = () => {
      closeInvoiceOrPaymentPage(routing, "replace");
    };

    const closeButtonProps: IButtonProps = {
      text: "Close",
      onClick: close
    };

    return (
      <Stack horizontal tokens={{ childrenGap: 8 }}>
        {!creditNote.isCancelled && (
          <SplitButton
            primary
            disabled={!creditNote.unallocated}
            disableContextMenuButton={!!creditNote.allocated}
            items={[
              {
                key: "cancel",
                text: CANCEL_TEXT,
                onClick: () => setShowCancelCreditDialog(true)
              }
            ]}
            text="Refund"
            onClick={() => setRefundDialogVisible(true)}
          />
        )}
        <DefaultButton {...closeButtonProps} />
        <CancelTransactionDialog
          hidden={!showCancelCreditDialog}
          subTextJSX={
            <Text>{`Cancelling credit ${creditNote.number} of ${currencyFormat(
              creditNote.total
            )} `}</Text>
          }
          transactionType="credit"
          onCancel={() => setShowCancelCreditDialog(false)}
          onConfirm={reason => handleCancel(reason)}
        />
        <RefundConfirmationDialog
          refundItemType="credit"
          refundDialogVisible={refundDialogVisible}
          onDismiss={onClear}
          onSucceeded={(refundPaymentMethod: PaymentMethod) =>
            onSucceeded(refundPaymentMethod)
          }
          refundTotal={creditNote.total - (creditNote.allocated || 0)}
          refundItemNumber={creditNote.number}
        />
      </Stack>
    );
  });
